import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  IconButton,
  Alert,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PlayArrow as TestIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { AlertWebhook, SubscriptionInfo, AlertKind } from '../../types/dashboard';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import ReauthDialog from '../common/ReauthDialog';

/** Dark palette matching the redesigned trade builder, so this dialog feels like part of the same flow. */
const builderDialogTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#4d8bff' },
    success: { main: '#10b981' },
    warning: { main: '#f59e0b' },
    error: { main: '#ef4444' },
    background: { default: '#0b1220', paper: '#19233b' },
    text: { primary: '#e8edf7', secondary: '#a6b2c8' },
    divider: '#2c3b5e',
  },
  typography: { fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  shape: { borderRadius: 8 },
});

const SERIF = "'Fraunces', Georgia, serif";

interface MultipleWebhooksManagerProps {
  /** Dialog visibility. Ignored when ``embedded`` is true. */
  open?: boolean;
  /** Close handler for the dialog. Not used when ``embedded`` is true. */
  onClose?: () => void;
  alertId: number;
  alertName: string; // e.g., "AAPL - Call"
  /** When bidirectional_multi, webhooks are grouped and tagged with bmta_execution_group */
  alertKind?: AlertKind;
  /** Kept for callers. Directional trades use one shared exit, not one per indicator. */
  triggerCount?: number;
  /** Render inline on the page (no modal dialog) instead of as a pop-up. */
  embedded?: boolean;
}

const BMTA_GROUP_META: { key: 'up' | 'down' | 'break'; title: string; subtitle: string }[] = [
  { key: 'up', title: 'When all agree bullish \u2014 go long', subtitle: 'Sent the moment every indicator turns bullish.' },
  { key: 'down', title: 'When all agree bearish \u2014 go short', subtitle: 'Sent the moment every indicator turns bearish.' },
  { key: 'break', title: 'When they stop agreeing \u2014 exit', subtitle: 'Sent the moment your indicators are no longer all in agreement.' },
];

/** Friendly label for an execution group, used on row chips. */
function friendlyBmtaLabel(g: 'up' | 'down' | 'break'): string {
  return g === 'up' ? 'Go long' : g === 'down' ? 'Go short' : 'Exit';
}

function bmtaGroupOf(w: AlertWebhook): 'up' | 'down' | 'break' | null {
  const g = w.bmta_execution_group ?? w.bmtaExecutionGroup;
  if (g === 'up' || g === 'down' || g === 'break') return g;
  return null;
}

function dmtaGroupOf(w: AlertWebhook): 'completion' | 'exit' | null {
  const g = (w.dmta_execution_group ?? w.dmtaExecutionGroup ?? '').toString().trim().toLowerCase();
  if (g === 'completion') return 'completion';
  // Older alerts stored a separate exit per indicator. Those all belong to the one exit.
  if (g === 'exit' || /^slot_out_\d+$/.test(g)) return 'exit';
  return null;
}

const DMTA_GROUP_META: { key: 'completion' | 'exit'; title: string; subtitle: string }[] = [
  {
    key: 'completion',
    title: 'When all indicators line up \u2014 enter',
    subtitle: 'Sent when every indicator is active. Add as many destinations as you need.',
  },
  {
    key: 'exit',
    title: 'When any indicator says exit',
    subtitle: 'One exit for the trade. Sent when any indicator turns off. Add as many destinations as you need.',
  },
];

const CUSTOM_PAYLOAD_MAX_BYTES = 8192;

interface WebhookFormData {
  webhook_url: string;
  webhook_name?: string;
  is_active: boolean;
  is_primary: boolean;
  description?: string;
  custom_payload?: string;
  bmta_execution_group?: 'up' | 'down' | 'break' | null;
  dmta_execution_group?: string | null;
}

const MultipleWebhooksManager: React.FC<MultipleWebhooksManagerProps> = ({
  open = false,
  onClose,
  alertId,
  alertName,
  alertKind,
  embedded = false,
}) => {
  const isBmta = alertKind === 'bidirectional_multi';
  const isDmta = alertKind === 'directional_multi';
  const { token } = useAuth();
  const [webhooks, setWebhooks] = useState<AlertWebhook[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [addingWebhook, setAddingWebhook] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<AlertWebhook | null>(null);
  const [formData, setFormData] = useState<WebhookFormData>({
    webhook_url: '',
    webhook_name: '',
    is_active: true,
    is_primary: false,
    description: '',
    custom_payload: '',
    bmta_execution_group: null,
  });
  const [error, setError] = useState<string | null>(null);
  const [testingWebhookId, setTestingWebhookId] = useState<number | null>(null);
  const formRef = useRef<HTMLDivElement | null>(null);
  const [payloadPresent, setPayloadPresent] = useState(false);
  const [payloadRevealed, setPayloadRevealed] = useState(false);
  const [payloadDirty, setPayloadDirty] = useState(false);
  const [reauthOpen, setReauthOpen] = useState(false);

  useEffect(() => {
    if ((open || embedded) && alertId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when dialog opens or inline mounts
  }, [open, embedded, alertId]);

  // Bring the add/edit form into view when it opens, so clicking Edit/Add is clearly responsive.
  useEffect(() => {
    if (addingWebhook && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [addingWebhook, editingWebhook]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load subscription info (optional - may not exist for all users)
      try {
        const subResponse = await apiService.getSubscription(token || undefined);
        if (subResponse.success && subResponse.data) {
          setSubscription({
            tier: subResponse.data.tier as SubscriptionInfo['tier'],
            webhook_limit: subResponse.data.webhook_limit,
            status: subResponse.data.status as SubscriptionInfo['status'],
          });
        }
      } catch (subError: any) {
        // If no subscription, use default values (basic tier)
        // Only log if it's not a 404 (expected when user has no subscription)
        if (subError?.status !== 404) {
          console.log('Error loading subscription, using default values:', subError);
        }
        setSubscription({
          tier: 'basic',
          webhook_limit: 1,
          status: 'active',
        });
      }

      // Load webhooks for this alert
      const webhooksResponse = await apiService.getAlertWebhooks(alertId, token || undefined);
      if (webhooksResponse.success && webhooksResponse.data) {
        setWebhooks(webhooksResponse.data);
      } else {
        setError(webhooksResponse.error || 'Failed to load webhooks');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddWebhook = (presetGroup?: 'up' | 'down' | 'break' | string | null) => {
    setEditingWebhook(null);
    setPayloadPresent(false);
    setPayloadRevealed(false);
    setPayloadDirty(false);
    setFormData({
      webhook_url: '',
      webhook_name: '',
      is_active: true,
      is_primary: false,
      description: '',
      custom_payload: '',
      bmta_execution_group: isBmta ? (presetGroup as 'up' | 'down' | 'break' | null) ?? null : null,
      dmta_execution_group: isDmta ? (typeof presetGroup === 'string' ? presetGroup : null) : null,
    });
    setAddingWebhook(true);
  };

  const handleEditWebhook = (webhook: AlertWebhook) => {
    setEditingWebhook(webhook);
    const present = Boolean(webhook.payload_present || webhook.custom_payload);
    setPayloadPresent(present);
    setPayloadRevealed(false);
    setPayloadDirty(false);
    setFormData({
      webhook_url: webhook.webhook_url,
      webhook_name: webhook.webhook_name || '',
      is_active: webhook.is_active,
      is_primary: webhook.is_primary,
      description: webhook.description || '',
      // Payload is masked in API responses — leave blank until re-auth reveal.
      custom_payload: '',
      bmta_execution_group: bmtaGroupOf(webhook),
      dmta_execution_group: dmtaGroupOf(webhook),
    });
    setAddingWebhook(true);
  };

  const handleRevealPayload = async (stepUpToken: string) => {
    if (!editingWebhook) return;
    try {
      const res = await apiService.revealAlertWebhookPayload(
        editingWebhook.id,
        stepUpToken,
        token || undefined
      );
      if (res.success) {
        setFormData((prev) => ({
          ...prev,
          custom_payload: res.data?.custom_payload ?? '',
        }));
        setPayloadRevealed(true);
        setPayloadDirty(false);
      } else {
        setError(res.error || 'Failed to reveal payload');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to reveal payload');
    }
  };

  const handleSaveWebhook = async () => {
    setError(null);

    // Validation
    if (!formData.webhook_url.trim()) {
      setError('A destination URL is required');
      return;
    }

    if (!formData.webhook_url.startsWith('http://') && !formData.webhook_url.startsWith('https://')) {
      setError('The destination URL must start with http:// or https://');
      return;
    }

    if (isBmta && !formData.bmta_execution_group) {
      setError('Choose when this destination should run (go long, go short, or exit).');
      return;
    }

    if (isDmta && !formData.dmta_execution_group) {
      setError('Choose when this destination should run (enter or exit).');
      return;
    }

    // Check subscription limit
    const currentActiveCount = webhooks.filter(w => w.is_active).length;
    if (!editingWebhook && subscription && subscription.webhook_limit < 999) {
      // If adding new webhook
      if (currentActiveCount >= subscription.webhook_limit) {
        setError(`Webhook limit exceeded. Your ${subscription.tier} subscription allows ${subscription.webhook_limit} webhooks per alert.`);
        return;
      }
    }

    // Custom payload max 8KB (UTF-8)
    const payload = formData.custom_payload?.trim() ?? '';
    if (payload.length > 0) {
      const byteCount = new TextEncoder().encode(payload).length;
      if (byteCount > CUSTOM_PAYLOAD_MAX_BYTES) {
        setError(`Custom payload must be at most 8KB (${CUSTOM_PAYLOAD_MAX_BYTES} bytes).`);
        return;
      }
    }

    try {
      if (editingWebhook) {
        // Update existing webhook. Only send custom_payload when revealed/edited
        // so we do not wipe a masked stored payload with null.
        const payload = formData.custom_payload?.trim();
        const updateBody: Parameters<typeof apiService.updateAlertWebhook>[1] = {
          webhook_url: formData.webhook_url,
          webhook_name: formData.webhook_name,
          is_active: formData.is_active,
          is_primary: formData.is_primary,
          description: formData.description,
          ...(isBmta ? { bmta_execution_group: formData.bmta_execution_group ?? null } : {}),
          ...(isDmta ? { dmta_execution_group: formData.dmta_execution_group ?? null } : {}),
        };
        if (payloadDirty || payloadRevealed || !payloadPresent) {
          updateBody.custom_payload = payload === '' ? null : payload;
        }
        const response = await apiService.updateAlertWebhook(
          editingWebhook.id,
          updateBody,
          token || undefined
        );
        if (response.success) {
          await loadData();
          setAddingWebhook(false);
          setEditingWebhook(null);
        } else {
          setError(response.error || 'Failed to update webhook');
        }
      } else {
        // Create new webhook
        const payload = formData.custom_payload?.trim();
        const response = await apiService.createAlertWebhook(
          alertId,
          {
            webhook_url: formData.webhook_url,
            webhook_name: formData.webhook_name,
            is_active: formData.is_active,
            is_primary: formData.is_primary,
            description: formData.description,
            custom_payload: payload === '' ? null : payload,
            ...(isBmta ? { bmta_execution_group: formData.bmta_execution_group ?? null } : {}),
            ...(isDmta ? { dmta_execution_group: formData.dmta_execution_group ?? null } : {}),
          },
          token || undefined
        );
        if (response.success) {
          await loadData();
          setAddingWebhook(false);
        } else {
          setError(response.error || 'Failed to create webhook');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save webhook');
    }
  };

  const handleDeleteWebhook = async (webhookId: number) => {
    if (!window.confirm('Are you sure you want to delete this webhook?')) {
      return;
    }

    try {
      const response = await apiService.deleteAlertWebhook(webhookId, token || undefined);
      if (response.success) {
        await loadData();
      } else {
        setError(response.error || 'Failed to delete webhook');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete webhook');
    }
  };

  const handleTestWebhook = async (webhookId: number) => {
    setTestingWebhookId(webhookId);
    try {
      const response = await apiService.testAlertWebhook(webhookId, token || undefined);
      if (response.success) {
        await loadData(); // Refresh to get updated test results
      } else {
        setError(response.error || 'Failed to test webhook');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to test webhook');
    } finally {
      setTestingWebhookId(null);
    }
  };

  const activeWebhookCount = webhooks.filter(w => w.is_active).length;
  const webhookLimit = subscription?.webhook_limit || 1;
  const unlimitedDestinations = webhookLimit >= 999;
  const canAddMore = unlimitedDestinations || activeWebhookCount < webhookLimit;

  const renderWebhookRow = (webhook: AlertWebhook) => {
    const g = bmtaGroupOf(webhook);
    const dg = dmtaGroupOf(webhook);
    return (
      <Box
        key={webhook.id}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
          mb: 1,
          backgroundColor: webhook.is_primary ? 'action.selected' : 'background.paper',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="start" gap={1}>
          <Box flex={1} minWidth={0}>
            <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
              <Typography variant="subtitle1">
                {webhook.webhook_name || `Destination ${webhook.id}`}
              </Typography>
              {webhook.is_primary && <Chip label="Primary" size="small" color="primary" />}
              {isBmta && g && (
                <Chip label={friendlyBmtaLabel(g)} size="small" variant="outlined" />
              )}
              {isDmta && dg && (
                <Chip
                  label={dg === 'completion' ? 'Enter' : 'Exit'}
                  size="small"
                  variant="outlined"
                />
              )}
              <Chip
                label={webhook.is_active ? 'Active' : 'Inactive'}
                size="small"
                color={webhook.is_active ? 'success' : 'default'}
              />
              {webhook.last_test_status && (
                <Chip
                  icon={
                    webhook.last_test_status === 'success' ? (
                      <CheckCircleIcon />
                    ) : (
                      <CancelIcon />
                    )
                  }
                  label={webhook.last_test_status === 'success' ? 'Test OK' : 'Test Failed'}
                  size="small"
                  color={webhook.last_test_status === 'success' ? 'success' : 'error'}
                />
              )}
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 0.5, wordBreak: 'break-all', overflowWrap: 'anywhere' }}
            >
              {webhook.webhook_url}
            </Typography>
            {webhook.description && (
              <Typography variant="body2" color="text.secondary">
                {webhook.description}
              </Typography>
            )}
            {(webhook.payload_present || (webhook.custom_payload && webhook.custom_payload.trim())) && (
              <Chip
                label="Transmitted payload set (masked)"
                size="small"
                sx={{ mt: 0.5 }}
                title="Re-authenticate when editing to view or change the payload"
              />
            )}
            {webhook.last_tested_at && (
              <Typography variant="caption" color="text.secondary">
                Last tested: {new Date(webhook.last_tested_at).toLocaleString()}
                {webhook.last_test_execution_time !== null &&
                  ` (${webhook.last_test_execution_time}ms)`}
              </Typography>
            )}
          </Box>
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Send a test to this destination">
              <IconButton
                size="small"
                onClick={() => handleTestWebhook(webhook.id)}
                disabled={testingWebhookId === webhook.id}
              >
                {testingWebhookId === webhook.id ? <CircularProgress size={20} /> : <TestIcon />}
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => handleEditWebhook(webhook)}>
              <EditIcon />
            </IconButton>
            <IconButton size="small" onClick={() => handleDeleteWebhook(webhook.id)} color="error">
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>
    );
  };

  const bodyContent = (
    <>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Subscription Info */}
            {subscription && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Plan:</strong> {subscription.tier.toUpperCase()} •{' '}
                  <strong>Destinations used:</strong>{' '}
                  {unlimitedDestinations ? `${activeWebhookCount} / Unlimited` : `${activeWebhookCount} / ${webhookLimit}`}
                  {!canAddMore && (
                    <span style={{ color: '#d32f2f', marginLeft: '8px' }}>
                      (Limit reached)
                    </span>
                  )}
                </Typography>
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Webhooks List */}
            <Box mb={2}>
              {isDmta ? (
                <>
                  <Typography variant="h6" sx={{ mb: 1, fontFamily: SERIF, fontWeight: 500 }}>
                    Execution destinations ({webhooks.length})
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Enter and exit each have their own destinations. Exit is one condition: it runs when{' '}
                    <strong>any</strong> indicator says exit, and you can send that exit to more than one place.
                  </Alert>
                  {DMTA_GROUP_META.map((meta) => {
                    const inGroup = webhooks.filter((w) => dmtaGroupOf(w) === meta.key);
                    return (
                      <Box key={meta.key} sx={{ mb: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} mb={1}>
                          <Box>
                            <Typography variant="subtitle1">{meta.title}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {meta.subtitle}
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleAddWebhook(meta.key)}
                            disabled={!canAddMore && !editingWebhook}
                          >
                            Add a destination
                          </Button>
                        </Box>
                        {inGroup.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ pl: 0.5 }}>
                            Nothing here yet.
                          </Typography>
                        ) : (
                          inGroup.map((w) => renderWebhookRow(w))
                        )}
                      </Box>
                    );
                  })}
                  {(() => {
                    const loose = webhooks.filter((w) => !dmtaGroupOf(w));
                    if (loose.length === 0) return null;
                    return (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1 }}>
                          Unassigned destinations (pick enter or exit via Edit)
                        </Typography>
                        {loose.map((w) => renderWebhookRow(w))}
                      </Box>
                    );
                  })()}
                </>
              ) : isBmta ? (
                <>
                  <Typography variant="h6" sx={{ mb: 1, fontFamily: SERIF, fontWeight: 500 }}>
                    Execution destinations ({webhooks.length})
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Add a destination for each outcome below: going <strong>long</strong>, going <strong>short</strong>,
                    and <strong>exiting</strong> when your indicators stop agreeing.
                  </Alert>
                  {BMTA_GROUP_META.map((meta) => {
                    const inGroup = webhooks.filter((w) => bmtaGroupOf(w) === meta.key);
                    return (
                      <Box key={meta.key} sx={{ mb: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} mb={1}>
                          <Box>
                            <Typography variant="subtitle1">{meta.title}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {meta.subtitle}
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleAddWebhook(meta.key)}
                            disabled={!canAddMore && !editingWebhook}
                          >
                            Add a destination
                          </Button>
                        </Box>
                        {inGroup.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ pl: 0.5 }}>
                            Nothing here yet.
                          </Typography>
                        ) : (
                          inGroup.map((w) => renderWebhookRow(w))
                        )}
                      </Box>
                    );
                  })}
                  {(() => {
                    const loose = webhooks.filter((w) => !bmtaGroupOf(w));
                    if (loose.length === 0) return null;
                    return (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1 }}>
                          Unassigned destinations (pick an outcome via Edit)
                        </Typography>
                        {loose.map((w) => renderWebhookRow(w))}
                      </Box>
                    );
                  })()}
                  {webhooks.length === 0 && !addingWebhook && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      Use <strong>Add a destination</strong> under each outcome above to get started.
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" sx={{ fontFamily: SERIF, fontWeight: 500 }}>
                      Destinations ({webhooks.length})
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddWebhook()}
                      disabled={!canAddMore && !editingWebhook}
                      size="small"
                    >
                      Add a destination
                    </Button>
                  </Box>

                  {webhooks.length === 0 && !addingWebhook && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                      No destinations yet. Click &quot;Add a destination&quot; to get started.
                    </Typography>
                  )}

                  {webhooks.map((webhook) => renderWebhookRow(webhook))}
                </>
              )}
            </Box>

            {/* Add/Edit Form */}
            {addingWebhook && (
              <Box ref={formRef}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontFamily: SERIF, fontWeight: 500 }}>
                  {editingWebhook ? 'Edit destination' : 'Add a destination'}
                </Typography>
                <TextField
                  fullWidth
                  label="Destination URL"
                  value={formData.webhook_url}
                  onChange={(e) =>
                    setFormData({ ...formData, webhook_url: e.target.value })
                  }
                  margin="normal"
                  required
                  placeholder="https://your-broker-or-bot.com/..."
                  helperText="Where orders are sent. Must start with http:// or https://"
                />
                {isBmta && (
                  <FormControl fullWidth margin="normal" required>
                    <InputLabel id="bmta-group-label">When should this run?</InputLabel>
                    <Select
                      labelId="bmta-group-label"
                      label="When should this run?"
                      value={formData.bmta_execution_group ?? ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          bmta_execution_group: (e.target.value || null) as 'up' | 'down' | 'break' | null,
                        })
                      }
                    >
                      <MenuItem value="up">When all agree bullish (go long)</MenuItem>
                      <MenuItem value="down">When all agree bearish (go short)</MenuItem>
                      <MenuItem value="break">When they stop agreeing (exit)</MenuItem>
                    </Select>
                  </FormControl>
                )}
                {isDmta && (
                  <FormControl fullWidth margin="normal" required>
                    <InputLabel id="dmta-group-label">When should this run?</InputLabel>
                    <Select
                      labelId="dmta-group-label"
                      label="When should this run?"
                      value={formData.dmta_execution_group ?? ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          dmta_execution_group: e.target.value || null,
                        })
                      }
                    >
                      {DMTA_GROUP_META.map((m) => (
                        <MenuItem key={m.key} value={m.key}>
                          {m.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <TextField
                  fullWidth
                  label="Destination name (optional)"
                  value={formData.webhook_name}
                  onChange={(e) =>
                    setFormData({ ...formData, webhook_name: e.target.value })
                  }
                  margin="normal"
                  placeholder="e.g. My trading bot"
                  helperText="A friendly name to recognize this destination"
                />
                <TextField
                  fullWidth
                  label="Notes (optional)"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  margin="normal"
                  multiline
                  rows={2}
                  placeholder="Any notes about this destination"
                />
                {editingWebhook && payloadPresent && !payloadRevealed && !payloadDirty && (
                  <Box sx={{ mt: 1, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Stored payload is masked. Re-authenticate to view or edit it.
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => setReauthOpen(true)}
                    >
                      Reveal
                    </Button>
                  </Box>
                )}
                <TextField
                  fullWidth
                  label="JSON payload or broker information (optional)"
                  value={formData.custom_payload ?? ''}
                  onChange={(e) => {
                    setPayloadDirty(true);
                    setFormData({ ...formData, custom_payload: e.target.value });
                  }}
                  margin="normal"
                  multiline
                  rows={3}
                  placeholder={
                    editingWebhook && payloadPresent && !payloadRevealed
                      ? 'Reveal to edit, or type a new payload to replace'
                      : 'Leave blank unless needed, e.g. {"qty": 1}'
                  }
                  helperText="Encrypted at rest. Masked on display; reveal requires re-authentication. Must be valid JSON if used."
                  inputProps={{ maxLength: 8192 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_active}
                      onChange={(e) =>
                        setFormData({ ...formData, is_active: e.target.checked })
                      }
                    />
                  }
                  label="Active"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_primary}
                      onChange={(e) =>
                        setFormData({ ...formData, is_primary: e.target.checked })
                      }
                    />
                  }
                  label="Primary destination"
                />
                <Box mt={2}>
                  <Button
                    variant="contained"
                    onClick={handleSaveWebhook}
                    sx={{ mr: 1 }}
                  >
                    Save
                  </Button>
                  <Button
                    onClick={() => {
                      setAddingWebhook(false);
                      setEditingWebhook(null);
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
          </>
        )}
    </>
  );

  const header = (
    <Box>
      <Typography variant="h5" sx={{ fontFamily: SERIF, fontWeight: 500 }}>
        Execution destinations
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {alertName}
      </Typography>
    </Box>
  );

  const reauthDialog = (
    <ReauthDialog
      open={reauthOpen}
      onClose={() => setReauthOpen(false)}
      onSuccess={handleRevealPayload}
      description="Re-enter your account password to view this webhook’s transmitted payload."
    />
  );

  if (embedded) {
    return (
      <ThemeProvider theme={builderDialogTheme}>
        <Box
          sx={{
            backgroundColor: '#111a2e',
            border: '1px solid #2c3b5e',
            borderRadius: 2,
            p: { xs: 2, sm: 3 },
            color: 'text.primary',
          }}
        >
          <Box sx={{ mb: 2 }}>{header}</Box>
          {bodyContent}
        </Box>
        {reauthDialog}
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={builderDialogTheme}>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { backgroundColor: '#111a2e', backgroundImage: 'none', border: '1px solid #2c3b5e' } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {header}
          <IconButton onClick={() => onClose?.()} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {bodyContent}
      </DialogContent>

      <DialogActions>
        <Button onClick={() => onClose?.()}>Close</Button>
      </DialogActions>
    </Dialog>
    {reauthDialog}
    </ThemeProvider>
  );
};

export default MultipleWebhooksManager;
